import type {
  CreateArtifactRequest,
  CreateArtifactResponse,
  UpdateArtifactRequest,
  UpdateArtifactResponse,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/artifact_service_pb';
import type {
  BatchDeleteExperimentRequest,
  BatchDeleteExperimentResponse,
  CreateExperimentRequest,
  CreateExperimentResponse,
  GetExperimentRequest,
  GetExperimentResponse,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/experiment_service_pb';
import type {
  ListProjectsRequest,
  ListProjectsResponse,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/project_service_pb';
import {
  Operator,
  type Property,
  type PropertyConstraint,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/query_pb';
import type {
  BatchDeleteRegisteredModelRequest,
  BatchDeleteRegisteredModelResponse,
  DeleteRegisteredModelRequest,
  DeleteRegisteredModelResponse,
  GetRegisteredModelRequest,
  GetRegisteredModelResponse,
  ListRegisteredModelsRequest,
  ListRegisteredModelsResponse,
  UpdateRegisteredModelRequest,
  UpdateRegisteredModelResponse,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_service_pb';
import type {
  BatchDeleteModelVersionRequest,
  BatchDeleteModelVersionResponse,
  CreateModelVersionRequest,
  CreateModelVersionResponse,
  DeleteModelVersionRequest,
  DeleteModelVersionResponse,
  ListModelVersionsForModelRequest,
  ListModelVersionsForModelResponse,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_version_service_pb';
import type {
  ListTagsRequest,
  ListTagsResponse,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/tag_service_pb.js';
import type { Value } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/value_pb';
import { ArtifactService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/artifact_service_connect';
import { ExperimentService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/experiment_service_connect';
import { ProjectService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/project_service_connect';
import { RegisteredModelService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/registered_model_service_connect';
import { RegisteredModelVersionService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/registered_model_version_service_connect';
import { TagService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/tag_service_connect';
import type { PlainMessage } from '@bufbuild/protobuf';
import { type CallOptions, createClient } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';

import { createConcurrencyLimiter } from './concurrencyLimiter';

export const ENDPOINTS = {
  storage: '/storage',
  ingest: '/ingest',
  deployment: '/deployer',
  deploymentServer: '/deploymentServer',
};

export const getFieldProperty = (fieldName: string): PlainMessage<Property> => {
  return {
    propertyType: {
      value: fieldName,
      case: 'field',
    },
  };
};

export const getPropertyConstraint = <FieldNameConstraint extends string>(
  fieldName: FieldNameConstraint,
  operator: Operator,
  value: Value['value']
): PlainMessage<PropertyConstraint> => {
  return {
    property: getFieldProperty(fieldName),
    operator,
    value: { value },
  };
};

// Experiments

export type BatchDeleteExperimentRequestPlain = PlainMessage<BatchDeleteExperimentRequest>;
export const batchDeleteExperiment = (
  mlopsApiUrl: string,
  request: BatchDeleteExperimentRequestPlain,
  options?: CallOptions
): Promise<BatchDeleteExperimentResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const experimentClient = createClient(ExperimentService, storageTransport);
  return experimentClient.batchDeleteExperiment(request, options);
};

export type CreateExperimentRequestPlain = PlainMessage<CreateExperimentRequest>;
export const createExperiment = (
  mlopsApiUrl: string,
  request: CreateExperimentRequestPlain,
  options?: CallOptions
): Promise<CreateExperimentResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const experimentClient = createClient(ExperimentService, storageTransport);
  return experimentClient.createExperiment(request, options);
};

export type GetExperimentRequestPlain = PlainMessage<GetExperimentRequest>;
export const getExperiment = (
  mlopsApiUrl: string,
  request: GetExperimentRequestPlain,
  options?: CallOptions
): Promise<GetExperimentResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const experimentClient = createClient(ExperimentService, storageTransport);
  return experimentClient.getExperiment(request, options);
};

// Artifacts

export type CreateArtifactRequestPlain = PlainMessage<CreateArtifactRequest>;
export const createArtifact = (
  mlopsApiUrl: string,
  request: CreateArtifactRequestPlain,
  options?: CallOptions
): Promise<CreateArtifactResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const artifactClient = createClient(ArtifactService, storageTransport);
  return artifactClient.createArtifact(request, options);
};

export type UpdateArtifactRequestPlain = PlainMessage<UpdateArtifactRequest>;
export const updateArtifact = (
  mlopsApiUrl: string,
  request: UpdateArtifactRequestPlain,
  options?: CallOptions
): Promise<UpdateArtifactResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const artifactClient = createClient(ArtifactService, storageTransport);
  return artifactClient.updateArtifact(request, options);
};

// Projects

export type ListProjectsRequestPlain = PlainMessage<ListProjectsRequest>;

export const listProjects = (
  mlopsApiUrl: string,
  request: ListProjectsRequestPlain,
  options?: CallOptions
): Promise<ListProjectsResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const projectClient = createClient(ProjectService, storageTransport);
  return projectClient.listProjects(request, options);
};

// Registered Models

export type BatchDeleteRegisteredModelRequestPlain = PlainMessage<BatchDeleteRegisteredModelRequest>;
export const batchDeleteRegisteredModel = (
  mlopsApiUrl: string,
  request: BatchDeleteRegisteredModelRequestPlain,
  options?: CallOptions
): Promise<BatchDeleteRegisteredModelResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const registeredModelClient = createClient(RegisteredModelService, storageTransport);
  return registeredModelClient.batchDeleteRegisteredModel(request, options);
};

export type DeleteRegisteredModelRequestPlain = PlainMessage<DeleteRegisteredModelRequest>;
export const deleteRegisteredModel = (
  mlopsApiUrl: string,
  request: DeleteRegisteredModelRequestPlain,
  options?: CallOptions
): Promise<DeleteRegisteredModelResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const registeredModelClient = createClient(RegisteredModelService, storageTransport);
  return registeredModelClient.deleteRegisteredModel(request, options);
};

/**
 * These fields are supported in the ListRegisteredModels request for filtering and ordering.
 * @see https://github.com/h2oai/mlops-storage/blob/d55de44506d6f2114deac6c12f2734d604886c9d/internal/database/dao/registered_model_sql.go#L36-L43
 */
export type ListRegisteredModelsSupportedFilterOrderKeys =
  | `created_time`
  | `description`
  | `id`
  | `last_modified_time`
  | `name`
  | `owner_id`
  | `status`
  | `updated_by`;

export type ListRegisteredModelsRequestPlain = PlainMessage<ListRegisteredModelsRequest>;
export const listRegisteredModels = (
  mlopsApiUrl: string,
  request: ListRegisteredModelsRequestPlain,
  options?: CallOptions
): Promise<ListRegisteredModelsResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const registeredModelClient = createClient(RegisteredModelService, storageTransport);
  return registeredModelClient.listRegisteredModels(request, options);
};

export type GetRegisteredModelRequestPlain = PlainMessage<GetRegisteredModelRequest>;
export const getRegisteredModel = (
  mlopsApiUrl: string,
  request: GetRegisteredModelRequestPlain,
  options?: CallOptions
): Promise<GetRegisteredModelResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const registeredModelClient = createClient(RegisteredModelService, storageTransport);
  return registeredModelClient.getRegisteredModel(request, options);
};

export type UpdateRegisteredModelRequestPlain = PlainMessage<UpdateRegisteredModelRequest>;
export const updateRegisteredModel = (
  mlopsApiUrl: string,
  request: UpdateRegisteredModelRequestPlain,
  options?: CallOptions
): Promise<UpdateRegisteredModelResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const registeredModelClient = createClient(RegisteredModelService, storageTransport);
  return registeredModelClient.updateRegisteredModel(request, options);
};

// Registered Model Versions

export type BatchDeleteModelVersionRequestPlain = PlainMessage<BatchDeleteModelVersionRequest>;
export const batchDeleteModelVersion = (
  mlopsApiUrl: string,
  request: BatchDeleteModelVersionRequestPlain,
  options?: CallOptions
): Promise<BatchDeleteModelVersionResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const registeredModelVersionClient = createClient(RegisteredModelVersionService, storageTransport);
  return registeredModelVersionClient.batchDeleteModelVersion(request, options);
};

export type CreateModelVersionRequestPlain = PlainMessage<CreateModelVersionRequest>;
export const createModelVersion = (
  mlopsApiUrl: string,
  request: CreateModelVersionRequestPlain,
  options?: CallOptions
): Promise<CreateModelVersionResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const registeredModelVersionClient = createClient(RegisteredModelVersionService, storageTransport);
  return registeredModelVersionClient.createModelVersion(request, options);
};

export type DeleteModelVersionRequestPlain = PlainMessage<DeleteModelVersionRequest>;
export const deleteModelVersion = (
  mlopsApiUrl: string,
  request: DeleteModelVersionRequestPlain,
  options?: CallOptions
): Promise<DeleteModelVersionResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const registeredModelVersionClient = createClient(RegisteredModelVersionService, storageTransport);
  return registeredModelVersionClient.deleteModelVersion(request, options);
};

export type ListModelVersionsForModelRequestPlain = PlainMessage<ListModelVersionsForModelRequest>;
const listModelVersionsForModelInternal = (
  mlopsApiUrl: string,
  request: ListModelVersionsForModelRequestPlain,
  options?: CallOptions
): Promise<ListModelVersionsForModelResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const registeredModelVersionClient = createClient(RegisteredModelVersionService, storageTransport);
  return registeredModelVersionClient.listModelVersionsForModel(request, options);
};

/**
 * Wrapped version of listModelVersionsForModel with concurrency limiting.
 * Maximum 5 concurrent requests are allowed to prevent overwhelming the backend.
 */
export const listModelVersionsForModel = createConcurrencyLimiter(listModelVersionsForModelInternal, 5);

// Tags

export type ListTagRequestPlain = PlainMessage<ListTagsRequest>;
export const listTags = (
  mlopsApiUrl: string,
  request: ListTagRequestPlain,
  options?: CallOptions
): Promise<ListTagsResponse> => {
  const storageTransport = createConnectTransport({
    baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
  });
  const tagClient = createClient(TagService, storageTransport);
  return tagClient.listTags(request, options);
};
